import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import { applyIconProps } from "@utils/icon";
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { NavItemType } from "types";
import {
  MENU_ITEM_ICON_SIZE,
  MENU_ITEM_ICON_STROKE,
  MENU_LIST_ITEM_VARIANTS
} from "../const";
import {
  MARQUEE_ATTR,
  SidebarGroupSeparator,
  SidebarRailDot,
  SidebarRow,
  SidebarRowChevron,
  SidebarRowCount,
  SidebarRowLabel,
  SidebarRowLabelText,
  SidebarRowPill
} from "../MainSidebar.components";

export interface Props {
  active: boolean;
  isDrawOpen: boolean;
  menuItem: NavItemType;
  variant: MENU_LIST_ITEM_VARIANTS;
  loading?: boolean;
  /** When true the row toggles its child list; it also navigates if it has a `url`. */
  expandable?: boolean;
  expanded?: boolean;
  /** A child of an open group. */
  nested?: boolean;
  onToggle?: () => void;
}

/** A number beside a label is a count; a word ("Beta") is a pill. */
const isCount = (badge: NavItemType["badge"]): boolean =>
  typeof badge === "number" || /^\d+$/.test(String(badge));

function MainSidebarMenuListItem({
  active,
  expandable = false,
  expanded = false,
  isDrawOpen,
  loading = false,
  menuItem,
  nested = false,
  onToggle,
  variant
}: Props) {
  const theme = useTheme();
  const { item } = theme.vars.ui.sidebar;
  const labelRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);

  useLayoutEffect(() => {
    const label = labelRef.current;
    const text = textRef.current;
    if (!label || !text) return;

    const measure = () =>
      setOverflow(Math.max(0, text.scrollWidth - label.clientWidth));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    observer.observe(text);
    return () => observer.disconnect();
  }, [menuItem.title]);

  if (variant === MENU_LIST_ITEM_VARIANTS.TITLE) {
    return (
      <SidebarGroupSeparator
        open={isDrawOpen}
        hasTitle={Boolean(menuItem.title)}
        data-test-id={menuItem.id}
      >
        {menuItem.title}
      </SidebarGroupSeparator>
    );
  }

  if (loading) {
    return (
      <SidebarRow
        disableRipple
        data-test-id={`${menuItem.id}-loading`}
        open={isDrawOpen}
        active={false}
        nested={nested}
      >
        <Skeleton
          variant="circular"
          width={MENU_ITEM_ICON_SIZE}
          height={MENU_ITEM_ICON_SIZE}
        />
        {isDrawOpen && (
          <Skeleton
            variant="text"
            width="80%"
            height={14}
            sx={{ borderRadius: "4px", transform: "none" }}
          />
        )}
      </SidebarRow>
    );
  }

  const badge = menuItem.badge;
  // Truthy only: a count of 0 is "nothing to report", not a badge reading "0"
  // — the same test the previous badge used.
  const hasBadge = Boolean(badge);
  const tooltip =
    typeof menuItem.title === "string" && hasBadge
      ? `${menuItem.title} · ${badge}`
      : menuItem.title;

  return (
    <BaseTooltip title={isDrawOpen ? "" : tooltip} placement="right">
      <SidebarRow
        component={Link}
        to={menuItem.url || ""}
        onClick={
          expandable
            ? (e: React.MouseEvent) => {
                // A group with a `url` navigates AND opens: the url is its
                // first child's, so the click lands somewhere real instead of
                // being a click with nothing behind it. A group without one
                // (Compliance) has nothing to navigate to, so the row stays a
                // pure toggle and the Link's empty `to` is cancelled.
                if (!menuItem.url) e.preventDefault();
                onToggle?.();
              }
            : undefined
        }
        disableRipple
        data-test-id={menuItem.id}
        aria-current={active ? "page" : undefined}
        open={isDrawOpen}
        active={active}
        nested={nested}
      >
        {menuItem.icon &&
          applyIconProps(menuItem.icon, {
            color: active ? item.selected.text : item.icon,
            size: MENU_ITEM_ICON_SIZE,
            strokeWidth: MENU_ITEM_ICON_STROKE
          })}

        <SidebarRowLabel visible={isDrawOpen} ref={labelRef}>
          <SidebarRowLabelText
            ref={textRef}
            {...{ [MARQUEE_ATTR]: "" }}
            style={
              isDrawOpen && overflow > 0
                ? ({
                    "--marquee-distance": `-${overflow}px`
                  } as React.CSSProperties)
                : undefined
            }
          >
            {menuItem.title}
          </SidebarRowLabelText>
        </SidebarRowLabel>

        {isDrawOpen && hasBadge ? (
          isCount(badge) ? (
            <SidebarRowCount>{badge}</SidebarRowCount>
          ) : (
            <SidebarRowPill>{badge}</SidebarRowPill>
          )
        ) : null}

        {isDrawOpen && expandable && (
          <SidebarRowChevron expanded={expanded}>
            <Icon name="chevron-down" size="xs" />
          </SidebarRowChevron>
        )}

        {!isDrawOpen && hasBadge ? <SidebarRailDot /> : null}
      </SidebarRow>
    </BaseTooltip>
  );
}

export default MainSidebarMenuListItem;
